import { useState, useMemo, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useI18n } from "@project/lib/use-i18n";
import { PageShell } from "@project/user/components/page-shell";
import { Reveal } from "@project/user/components/reveal";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import {
  ShoppingBag,
  Star,
  Quote,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Heart,
  Plus,
  Minus,
  Leaf,
  Loader2,
} from "lucide-react";
import {
  GruhUdhyogOrderModal,
  type CartItem,
} from "@project/user/components/gruh-udhyog-order-modal";
import { get } from "@project/services/shared/api-client";
import type { Product, ProductCategory, ProductVariant } from "@project/types/gruh-udhyog";
import { resolveMediaUrl } from "@project/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  addToCart as reduxAddToCart,
  updateQuantity as reduxUpdateQuantity,
  removeItem as reduxRemoveItem,
  clearCart as reduxClearCart,
} from "@/store/slices/cartSlice";

import careImg from "@project/assets/home-care.webp";
import residentsImg from "@project/assets/home-residents.webp";
import gaushalaImg1 from "@project/assets/gruh-udhyog/GU-banner.png";
import gaushalaImg2 from "@project/assets/Gaushala/image (2).jfif";
import methiKhakhraImg from "@project/assets/gruh-udhyog/GU-main.png";

const artisans = [
  {
    nameKey: "programs.gruhUdhyog.artisans.a1Name",
    craftKey: "programs.gruhUdhyog.artisans.a1Craft",
    yearsKey: "programs.gruhUdhyog.artisans.a1Years",
    image: careImg,
  },
  {
    nameKey: "programs.gruhUdhyog.artisans.a2Name",
    craftKey: "programs.gruhUdhyog.artisans.a2Craft",
    yearsKey: "programs.gruhUdhyog.artisans.a2Years",
    image: residentsImg,
  },
  {
    nameKey: "programs.gruhUdhyog.artisans.a3Name",
    craftKey: "programs.gruhUdhyog.artisans.a3Craft",
    yearsKey: "programs.gruhUdhyog.artisans.a3Years",
    image: gaushalaImg2,
  },
];

/** Product card matching the exact site card shape with backend model mapping */
function ProductCard({
  product,
  cart,
  onAddToCart,
  onUpdateQuantity,
}: {
  product: Product;
  cart: CartItem[];
  onAddToCart: (product: Product, variant: ProductVariant) => void;
  onUpdateQuantity: (cartItemId: string, delta: number) => void;
}) {
  const activeVariants = useMemo(
    () => (product.variants ?? []).filter(v => v.is_active !== false),
    [product.variants]
  );
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);

  const selectedVariant =
    activeVariants[
    Math.min(selectedVariantIdx, Math.max(activeVariants.length - 1, 0))
    ];

  const variantLabel = (v: ProductVariant) => v.value ?? v.weight ?? "";
  const variantStock = (v: ProductVariant) =>
    Number(v.available_stock ?? v.total_stock ?? v.stock ?? 0);
  const variantPrice = (v: ProductVariant) =>
    v.display_price ?? v.discounted_price ?? v.price;
  const hasDiscount = (v: ProductVariant) => {
    const discounted = Number(v.discounted_price);
    const base = Number(v.price);
    return (
      Number.isFinite(discounted) &&
      discounted > 0 &&
      Number.isFinite(base) &&
      discounted < base
    );
  };

  const cartItemId = selectedVariant
    ? `${product.id}-${variantLabel(selectedVariant).replace(/\s+/g, "")}`
    : `${product.id}-default`;
  const inCart = cart.find(item => item.id === cartItemId);

  if (!selectedVariant) {
    return null; // Don't render cards without variants
  }

  const stockLeft = variantStock(selectedVariant);
  const displayStockLeft = Math.max(0, stockLeft - (inCart?.quantity ?? 0));
  const isOutOfStock = stockLeft <= 0 || selectedVariant.in_stock === false;
  const isMaxStockReached = Boolean(inCart && inCart.quantity >= stockLeft);
  const imageSrc = resolveMediaUrl(product.image ?? product.image_url);
  const optionTitle = product.variant_master_details?.title?.trim() || "Weight";
  const displayPrice = variantPrice(selectedVariant);
  const showDiscount = hasDiscount(selectedVariant);

  return (
    <article className="border-gold/30 group flex h-full flex-col justify-between rounded-[2.25rem] border bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-md">
      <div>
        {/* Medium-height product image */}
        <div className="bg-secondary/30 relative h-44 w-full overflow-hidden rounded-2xl sm:h-48">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={product.product_name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="bg-muted/40 flex h-full w-full items-center justify-center">
              <ShoppingBag className="text-muted-foreground/30 h-10 w-10" />
            </div>
          )}
          {product.is_handmade && (
            <span className="absolute left-3 top-3 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-foreground shadow-sm">
              Handmade
            </span>
          )}
          {product.is_signature && (
            <span className="bg-card/90 absolute right-3 top-3 flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold text-amber-600 shadow-sm backdrop-blur-sm">
              <Star className="h-3.5 w-3.5 fill-current text-amber-500" />
              Signature
            </span>
          )}
        </div>

        {/* Category & Maker line */}
        <div className="mt-4 flex items-center justify-between">
          <Badge
            variant="secondary"
            className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold"
          >
            {product.category_name ??
              product.category_details?.category_name ??
              "Product"}
          </Badge>
          <span className="text-xs font-medium italic text-muted-foreground">
            by {product.is_handmade ? "Our Elders" : "Jivandhara Team"}
          </span>
        </div>

        {/* Title & Details */}
        <h3 className="mt-2.5 font-serif text-xl font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
          {product.product_name}
        </h3>
        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
          {product.short_description ??
            product.description ??
            "No description provided."}
        </p>

        {/* Variant option pills (from variant_master_details + variants[].value) */}
        <div className="mt-3">
          <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Select {optionTitle}:
          </span>
          <div className="flex flex-wrap gap-1.5">
            {activeVariants.map((v, idx) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setSelectedVariantIdx(idx)}
                className={`rounded-lg px-2.5 py-1 text-xs font-bold transition-all ${selectedVariantIdx === idx
                  ? "shadow-xs bg-primary text-primary-foreground"
                  : "bg-secondary/70 border-border/40 border text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
              >
                {variantLabel(v)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Price & Circular Add Action Footer */}
      <div className="border-border/60 mt-5 flex items-center justify-between border-t pt-4">
        <div>
          <span className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Price
          </span>
          <div className="flex items-baseline gap-2">
            <span className="font-sans text-xl font-bold text-primary">
              ₹{Number(displayPrice).toFixed(2)}
            </span>
            {showDiscount && (
              <span className="text-xs text-muted-foreground line-through">
                ₹{Number(selectedVariant.price).toFixed(2)}
              </span>
            )}
          </div>
          {(isOutOfStock || displayStockLeft <= 10) && (
            <span className="mt-0.5 block text-[10px] font-semibold text-amber-600">
              {isOutOfStock || displayStockLeft <= 0
                ? "Out of Stock"
                : `Stock: ${displayStockLeft} left`}
            </span>
          )}
        </div>

        {isOutOfStock ? (
          <Button
            type="button"
            disabled
            className="rounded-full bg-muted px-3 text-xs font-semibold text-muted-foreground"
          >
            Sold Out
          </Button>
        ) : inCart ? (
          <div className="bg-primary/10 flex items-center rounded-full border border-primary p-1">
            <button
              type="button"
              onClick={() => onUpdateQuantity(cartItemId, -1)}
              className="hover:bg-primary/20 grid h-8 w-8 place-items-center rounded-full text-primary transition-colors"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-7 text-center text-xs font-bold text-primary">
              {inCart.quantity}
            </span>
            <button
              type="button"
              disabled={isMaxStockReached}
              onClick={() => onUpdateQuantity(cartItemId, 1)}
              className="hover:bg-primary/20 grid h-8 w-8 place-items-center rounded-full text-primary transition-colors disabled:opacity-30"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onAddToCart(product, selectedVariant)}
            title="Add to Cart"
            className="hover:bg-primary/90 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-all hover:scale-105"
          >
            <Plus className="h-5 w-5" />
          </button>
        )}
      </div>
    </article>
  );
}

export default function GruhUdhyogShowcase() {
  const { t } = useI18n();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState<{ id: number | "All"; name: string }[]>([
    { id: "All", name: "All" },
  ]);
  const [selectedCatId, setSelectedCatId] = useState<number | "All">("All");
  const [sortBy, setSortBy] = useState<"popular" | "price-low" | "price-high">(
    "popular"
  );

  const dispatch = useAppDispatch();
  const currentUserKey = useAppSelector(state => state.cart.currentUserKey);
  const cart = useAppSelector(state => state.cart.carts[currentUserKey] || []);
  const [modalOpen, setModalOpen] = useState(false);

  // Check search query parameter to automatically reopen shopping bag after login redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("bag") === "open" && cart.length > 0) {
      setModalOpen(true);
      // Clean up search query parameter from URL bar to keep it tidy
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [cart.length]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const catRes = await get<{
          success: boolean;
          data: ProductCategory[];
        }>("/jivandhara/product-categories/");

        const activeCategories = (catRes?.data ?? [])
          .filter(c => c.is_active !== false)
          .map(c => ({ id: c.id, name: c.category_name }));

        setCategories([{ id: "All", name: "All" }, ...activeCategories]);
      } catch (err) {
        console.error("Failed to load categories:", err);
      }
    };
    void fetchCategories();
  }, []);

  // Fetch products when selectedCategory changes
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url =
          selectedCatId === "All"
            ? "/jivandhara/products/"
            : `/jivandhara/products/?category=${selectedCatId}`;
        const res = await get<{
          success: boolean;
          data: Product[];
          pagination?: unknown;
        }>(url);
        const active = (res?.data ?? []).filter(p => p.is_active !== false);
        setProducts(active);
      } catch (err) {
        console.error("Failed to load products:", err);
      } finally {
        setLoading(false);
      }
    };
    void fetchProducts();
  }, [selectedCatId]);

  // Cart helper functions
  const addToCart = (product: Product, variant: ProductVariant) => {
    dispatch(reduxAddToCart({ product, variant }));
  };

  const updateQuantity = (cartItemId: string, delta: number) => {
    dispatch(reduxUpdateQuantity({ cartItemId, delta }));
  };

  const removeItem = (cartItemId: string) => {
    dispatch(reduxRemoveItem(cartItemId));
  };

  const clearCart = () => {
    dispatch(reduxClearCart());
  };

  const totalCartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const totalCartAmount = cart.reduce(
    (sum, i) => sum + i.price * i.quantity,
    0
  );

  // Compute Signature Products (is_signature === true)
  const signatureProducts = useMemo(() => {
    return products.filter(p => p.is_signature === true);
  }, [products]);

  // Compute Catalog list (with filters and sorting)
  const catalogProducts = useMemo(() => {
    let result = products;

    if (sortBy === "price-low") {
      result = [...result].sort((a, b) => {
        const priceA = Number(
          a.variants?.[0]?.display_price ?? a.variants?.[0]?.price ?? 0
        );
        const priceB = Number(
          b.variants?.[0]?.display_price ?? b.variants?.[0]?.price ?? 0
        );
        return priceA - priceB;
      });
    } else if (sortBy === "price-high") {
      result = [...result].sort((a, b) => {
        const priceA = Number(
          a.variants?.[0]?.display_price ?? a.variants?.[0]?.price ?? 0
        );
        const priceB = Number(
          b.variants?.[0]?.display_price ?? b.variants?.[0]?.price ?? 0
        );
        return priceB - priceA;
      });
    }
    return result;
  }, [products, sortBy]);

  const scrollToCatalog = () => {
    document
      .getElementById("product-catalog")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell>
      {/* 1. HERO SECTION */}
      <section className="bg-hero-gradient relative overflow-hidden text-primary-foreground shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)]">
        <img
          src={gaushalaImg1}
          alt=""
          aria-hidden
          loading="eager"
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />
        <div className="from-primary/60 via-primary/45 to-gold/35 absolute inset-0 bg-gradient-to-br" />
        <div className="from-primary/50 to-primary/25 absolute inset-0 bg-gradient-to-t via-transparent" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:py-14 md:grid-cols-2 md:gap-12 md:py-16 lg:gap-16">
          <div className="text-left">
            <span className="bg-gold/25 border-gold/40 inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-gold">
              <Sparkles className="h-3.5 w-3.5" />
              {t("programs.gruhUdhyog.hero.badge")}
            </span>

            <h1 className="mt-5 font-serif text-4xl font-bold leading-tight drop-shadow sm:text-5xl md:text-[3.25rem]">
              {t("programs.gruhUdhyog.hero.brand")}
            </h1>

            <p className="mt-4 text-base font-semibold leading-snug text-gold sm:text-lg">
              {t("programs.gruhUdhyog.hero.title")}
            </p>

            <p className="text-primary-foreground/95 mt-4 max-w-xl text-sm leading-relaxed sm:text-base">
              {t("programs.gruhUdhyog.hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={scrollToCatalog}
                className="hover:bg-gold/90 h-11 gap-2 rounded-xl bg-gold px-6 text-xs font-bold uppercase tracking-wider text-gold-foreground shadow-lg"
              >
                <ShoppingBag className="h-4 w-4" />
                {t("programs.gruhUdhyog.hero.primaryCta")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  document
                    .getElementById("artisan-stories")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="border-primary-foreground/50 hover:bg-primary-foreground/10 h-11 gap-2 rounded-xl bg-transparent px-5 font-semibold text-primary-foreground hover:text-primary-foreground"
              >
                <Heart className="h-4 w-4" />
                {t("programs.gruhUdhyog.hero.secondaryCta")}
              </Button>
            </div>
          </div>

          {/* Hero Banner Feature Image Card */}
          <div className="relative flex justify-end">
            <div className="bg-card/10 w-full max-w-lg overflow-hidden rounded-2xl border border-white/20 p-6 shadow-2xl ring-1 ring-white/20 backdrop-blur-md">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={methiKhakhraImg}
                  alt="Fresh Handmade Methi Khakhra"
                  className="h-full w-full object-cover"
                />
                <span className="absolute left-3 top-3 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-foreground shadow-md">
                  Handcrafted with Love
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-white">
                <div className="flex items-center gap-2 rounded-lg bg-white/10 p-2.5 backdrop-blur-sm">
                  <Leaf className="h-4 w-4 shrink-0 text-gold" />
                  <span>100% Pure & Preservative Free</span>
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-white/10 p-2.5 backdrop-blur-sm">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-gold" />
                  <span>100% Proceeds to Charity</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Loading overlay for the whole store */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm font-medium text-muted-foreground">
              Loading fresh homemade snacks catalog…
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* 2. SIGNATURE PRODUCTS (is_signature === true) */}
          {signatureProducts.length > 0 && (
            <section className="mx-auto max-w-7xl px-4 py-14 sm:py-16">
              <Reveal className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                  Fresh Handmade Snacks
                </p>
                <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                  Our Signature Products
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Our absolute best sellers, handmade daily with premium local
                  ingredients.
                </p>
              </Reveal>

              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {signatureProducts.map((p, i) => (
                  <Reveal key={p.id} variant="zoom" delay={i * 80}>
                    <ProductCard
                      product={p}
                      cart={cart}
                      onAddToCart={addToCart}
                      onUpdateQuantity={updateQuantity}
                    />
                  </Reveal>
                ))}
              </div>
            </section>
          )}

          {/* 3. MAIN STORE CATALOG GRID */}
          <section
            id="product-catalog"
            className="border-border/40 bg-secondary/30 border-y py-14 sm:py-16"
          >
            <div className="mx-auto max-w-7xl px-4">
              <Reveal>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                    Store Catalog
                  </p>
                  <h2 className="mt-2 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                    Order Fresh Homemade Snacks
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Select your favorite snacks and place an online order
                    directly.
                  </p>
                </div>
              </Reveal>

              {/* Category Filters */}
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6">
                <div className="flex flex-wrap gap-2">
                  {categories.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCatId(c.id)}
                      className={`rounded-full px-4 py-2 text-xs font-bold transition ${selectedCatId === c.id
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-foreground/70 hover:border-primary/40 border border-border bg-card hover:text-primary"
                        }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>

                {/* Sort dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    Sort by:
                  </span>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value as typeof sortBy)}
                    className="rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="popular">Popularity</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                </div>
              </div>

              {/* Products Catalog Grid */}
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {catalogProducts.map((p, i) => (
                  <Reveal key={p.id} variant="zoom" delay={i * 50}>
                    <ProductCard
                      product={p}
                      cart={cart}
                      onAddToCart={addToCart}
                      onUpdateQuantity={updateQuantity}
                    />
                  </Reveal>
                ))}

                {catalogProducts.length === 0 && (
                  <div className="col-span-full py-16 text-center text-sm text-muted-foreground">
                    No products found in this category.
                  </div>
                )}
              </div>

              <div className="mt-10 rounded-2xl border border-border bg-card p-6 text-center">
                <p className="text-xs text-muted-foreground">
                  Looking for custom bulk orders or festive gift hampers?
                  <Link
                    to="/contact"
                    className="ml-1 inline-flex items-center gap-1 font-bold text-primary hover:underline"
                  >
                    Contact our team <ArrowRight className="h-3 w-3" />
                  </Link>
                </p>
              </div>
            </div>
          </section>
        </>
      )}

      {/* 5. ARTISAN STORIES */}
      <section
        id="artisan-stories"
        className="mx-auto max-w-7xl px-4 py-14 sm:py-16"
      >
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
            {t("programs.gruhUdhyog.artisans.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            {t("programs.gruhUdhyog.artisans.heading")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("programs.gruhUdhyog.artisans.subtitle")}
          </p>
        </Reveal>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {artisans.map((a, i) => (
            <Reveal key={a.nameKey} delay={i * 100}>
              <figure className="group flex h-full flex-col justify-between rounded-3xl border border-border bg-card p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-md">
                <div>
                  <div className="bg-secondary/40 relative aspect-[4/3] overflow-hidden rounded-2xl">
                    <img
                      src={a.image}
                      alt={t(a.nameKey)}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute bottom-3 left-3 rounded-full bg-black/60 px-3 py-1 text-[10px] font-bold text-white backdrop-blur-md">
                      {t(a.yearsKey)}
                    </span>
                  </div>

                  <Quote className="mt-5 h-6 w-6 text-gold" />
                  <blockquote className="mt-2 font-serif text-base italic leading-snug text-foreground">
                    {t("programs.gruhUdhyog.artisans.quote")}
                  </blockquote>
                </div>

                <figcaption className="mt-6 border-t border-border pt-4">
                  <div className="text-sm font-bold text-foreground">
                    {t(a.nameKey)}
                  </div>
                  <div className="text-xs font-medium text-primary">
                    {t(a.craftKey)}
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STICKY FLOATING CART BADGE */}
      {totalCartCount > 0 && (
        <div className="animate-in fade-in slide-in-from-bottom-4 fixed bottom-6 right-6 z-40 duration-300">
          <Button
            type="button"
            onClick={() => setModalOpen(true)}
            className="hover:bg-primary/90 h-14 gap-3 rounded-full border-2 border-white/20 bg-primary px-6 text-primary-foreground shadow-2xl"
          >
            <div className="relative">
              <ShoppingBag className="h-6 w-6 text-gold" />
              <span className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-gold text-[10px] font-bold text-gold-foreground">
                {totalCartCount}
              </span>
            </div>
            <div className="text-left leading-tight">
              <div className="text-[10px] font-bold uppercase text-gold">
                View Bag
              </div>
              <div className="text-sm font-bold">₹{totalCartAmount}</div>
            </div>
          </Button>
        </div>
      )}

      {/* CART & ORDER MODAL */}
      <GruhUdhyogOrderModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        cart={cart}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeItem}
        onClearCart={clearCart}
      />
    </PageShell>
  );
}
